import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { WebAuthLayout } from './WebAuthLayout';
import { useAuth } from './AuthContext';
import { ApiError, NetworkError } from '../api/client';
import type { CodeSentResult } from '../api/auth.types';
import { useSecondsLeft } from '../hooks/useSecondsLeft';
import { GOOGLE_CLIENT_ID, GoogleSignInButton } from './GoogleSignInButton';
import { NAV_PATH } from '../shell/nav';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#4285F4" d="M45.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h11.8c-.5 2.7-2 5-4.4 6.6v5.5h7.1c4.2-3.9 6.6-9.6 6.6-16.6z" />
    <path fill="#34A853" d="M24 46c5.9 0 10.9-2 14.5-5.4l-7.1-5.5c-2 1.3-4.5 2.1-7.4 2.1-5.7 0-10.5-3.8-12.2-9H4.5v5.7C8.1 40.9 15.4 46 24 46z" />
    <path fill="#FBBC05" d="M11.8 28.2c-.4-1.3-.7-2.7-.7-4.2s.2-2.9.7-4.2v-5.7H4.5C3 17 2 20.4 2 24s1 7 4.5 9.9l7.3-5.7z" />
    <path fill="#EA4335" d="M24 10.7c3.2 0 6.1 1.1 8.4 3.3l6.3-6.3C34.9 4.2 29.9 2 24 2 15.4 2 8.1 7.1 4.5 14.1l7.3 5.7c1.7-5.2 6.5-9.1 12.2-9.1z" />
  </svg>
);

const AppleIcon = () => (
  <svg width="16" height="18" viewBox="0 0 24 24" fill="#fff">
    <path d="M16.365 1.43c0 1.14-.462 2.24-1.216 3.05-.833.9-2.2 1.6-3.318 1.51-.14-1.1.42-2.26 1.19-3.02.86-.87 2.34-1.55 3.34-1.54zM20.7 17.24c-.5 1.13-.74 1.63-1.38 2.63-.9 1.4-2.16 3.14-3.73 3.15-1.4.01-1.76-.91-3.66-.9-1.9.01-2.3.92-3.7.9-1.57-.02-2.76-1.58-3.66-2.98-2.5-3.85-2.76-8.37-1.22-10.77 1.1-1.7 2.83-2.7 4.46-2.7 1.66 0 2.7.92 4.08.92 1.34 0 2.15-.92 4.08-.92 1.45 0 2.99.79 4.08 2.15-3.59 1.97-3 7.09.65 8.32z" />
  </svg>
);

const input = (invalid: boolean): CSSProperties => ({
  width: '100%',
  height: 50,
  borderRadius: 'var(--radius-control)',
  border: `1.5px solid ${invalid ? 'var(--error)' : 'var(--border-default)'}`,
  padding: '0 14px',
  fontSize: 15,
  fontFamily: 'var(--font-vi)',
  color: 'var(--ink)',
  outline: 'none',
  background: 'var(--surface-page)',
});

const label: CSSProperties = { fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' };

/**
 * Nút OAuth dạng tắt — Apple luôn tắt (chưa làm); Google chỉ tắt khi chưa đặt
 * VITE_GOOGLE_CLIENT_ID, giống apps/mobile/src/screens/auth/LoginScreen.tsx.
 */
function DisabledOAuthButton({ icon, children, dark }: { icon: ReactNode; children: ReactNode; dark?: boolean }) {
  return (
    <div
      title="Đăng nhập bằng Google/Apple sẽ có ở bản sau"
      style={{
        height: 50,
        borderRadius: 'var(--radius-control)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        fontFamily: 'var(--font-vi)',
        fontWeight: 600,
        fontSize: 14.5,
        opacity: 0.45,
        cursor: 'not-allowed',
        background: dark ? 'var(--navy-300)' : 'var(--surface-page)',
        border: dark ? 'none' : '1.5px solid var(--border-default)',
        color: dark ? '#fff' : 'var(--ink)',
      }}
    >
      {icon}
      {children}
    </div>
  );
}

/**
 * Các bước của màn xác thực — khớp apps/mobile/src/screens/auth/LoginScreen.tsx.
 * Đăng ký và quên mật khẩu đều 2 bước: gửi mã OTP về email (hết hạn 60s) rồi nhập mã.
 */
type Mode = 'login' | 'register' | 'verifyRegister' | 'forgot' | 'resetPassword';

const HEADINGS: Record<Mode, string> = {
  login: 'Chào mừng trở lại',
  register: 'Tạo tài khoản Torii',
  verifyRegister: 'Xác minh email',
  forgot: 'Quên mật khẩu',
  resetPassword: 'Đặt lại mật khẩu',
};

const SUBMIT_LABEL: Record<Mode, string> = {
  login: 'Đăng nhập',
  register: 'Đăng ký',
  verifyRegister: 'Xác nhận',
  forgot: 'Gửi mã',
  resetPassword: 'Đặt lại mật khẩu',
};

interface CodeTimers {
  expiresAt: number;
  expiresInSeconds: number;
  resendAt: number;
  resendAfterSeconds: number;
}

function timersFrom(res: CodeSentResult): CodeTimers {
  const now = Date.now();
  return {
    expiresAt: now + res.expiresInSeconds * 1000,
    expiresInSeconds: res.expiresInSeconds,
    resendAt: now + res.resendAfterSeconds * 1000,
    resendAfterSeconds: res.resendAfterSeconds,
  };
}

const linkButton: CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-vi)', fontWeight: 600 };

/** Ô nhập mã 6 số + đếm ngược hết hạn + "Gửi lại mã" — dùng chung cho xác minh đăng ký và đặt lại mật khẩu. */
function CodeField({ code, onChange, timers, resending, onResend, invalid }: { code: string; onChange: (v: string) => void; timers: CodeTimers; resending: boolean; onResend: () => void; invalid: boolean }) {
  const expiresIn = useSecondsLeft(timers.expiresAt, timers.expiresInSeconds);
  const resendIn = useSecondsLeft(timers.resendAt, timers.resendAfterSeconds);
  const canResend = resendIn === 0 && !resending;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor="code" style={label}>
        Mã xác minh
      </label>
      <input
        id="code"
        value={code}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        placeholder="6 chữ số"
        // Chỉ giãn chữ khi đã có số — placeholder giữ cỡ chữ như các ô khác.
        style={{ ...input(invalid), ...(code ? { fontSize: 20, letterSpacing: 6 } : {}) }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12.5 }}>
        <span style={{ color: expiresIn > 0 ? 'var(--text-secondary)' : 'var(--error)' }}>{expiresIn > 0 ? `Mã hết hạn sau ${expiresIn} giây` : 'Mã đã hết hạn'}</span>
        <button
          type="button"
          disabled={!canResend}
          onClick={onResend}
          style={{ ...linkButton, fontSize: 12.5, fontWeight: 700, color: canResend ? 'var(--cta-default)' : 'var(--text-disabled)', cursor: canResend ? 'pointer' : 'default' }}
        >
          {resending ? 'Đang gửi…' : resendIn > 0 ? `Gửi lại mã sau ${resendIn}s` : 'Gửi lại mã'}
        </button>
      </div>
    </div>
  );
}

export function WebLoginScreen() {
  const { status, login, loginWithGoogle, register, verifyRegistration, forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [timers, setTimers] = useState<CodeTimers | null>(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);

  // Đã đăng nhập sẵn (mở lại tab) thì vào thẳng lộ trình. Chỉ chạy lúc mount:
  // không được chạy lại khi status đổi do submit, vì lúc đó handler tự điều hướng.
  const bootStatus = useRef(status);
  useEffect(() => {
    if (bootStatus.current === 'authenticated') {
      navigate(NAV_PATH.hoc, { replace: true });
    }
  }, [navigate]);

  const goTo = (next: Mode) => {
    setErrorText(null);
    setMode(next);
  };

  /** Gửi (hoặc gửi lại) mã — đăng ký gọi lại /auth/register với cùng dữ liệu. */
  const sendCode = (forMode: 'verifyRegister' | 'resetPassword') =>
    forMode === 'verifyRegister' ? register(email, password, displayName || undefined) : forgotPassword(email);

  const showError = (err: unknown) =>
    setErrorText(err instanceof ApiError || err instanceof NetworkError ? err.message : 'Có lỗi xảy ra, thử lại sau.');

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (loading) return;
    setErrorText(null);
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        navigate(NAV_PATH.hoc, { replace: true });
      } else if (mode === 'register' || mode === 'forgot') {
        const next = mode === 'register' ? 'verifyRegister' : 'resetPassword';
        setTimers(timersFrom(await sendCode(next)));
        setCode('');
        setMode(next);
        setLoading(false);
      } else if (mode === 'verifyRegister') {
        await verifyRegistration(email, code);
        // Tài khoản vừa tạo — đi tiếp màn hồ sơ để đặt tên và mục tiêu học.
        navigate('/onboarding', { replace: true });
      } else {
        await resetPassword(email, code, newPassword);
        navigate(NAV_PATH.hoc, { replace: true });
      }
    } catch (err) {
      showError(err);
      setLoading(false);
    }
  };

  const resend = async () => {
    if (mode !== 'verifyRegister' && mode !== 'resetPassword') return;
    setErrorText(null);
    setResending(true);
    try {
      setTimers(timersFrom(await sendCode(mode)));
      setCode('');
    } catch (err) {
      showError(err);
    } finally {
      setResending(false);
    }
  };

  const signInWithGoogle = async (idToken: string) => {
    if (loading) return;
    setErrorText(null);
    setLoading(true);
    try {
      const { isNewUser } = await loginWithGoogle(idToken);
      // Tài khoản vừa tạo từ Google → màn hồ sơ để đặt mục tiêu học, giống đăng ký email.
      navigate(isNewUser ? '/onboarding' : NAV_PATH.hoc, { replace: true });
    } catch (err) {
      showError(err);
      setLoading(false);
    }
  };

  const isLogin = mode === 'login';
  const isCodeStep = mode === 'verifyRegister' || mode === 'resetPassword';
  const showEmail = mode === 'login' || mode === 'register' || mode === 'forgot';
  const showPassword = mode === 'login' || mode === 'register';
  const submitDisabled = loading || (isCodeStep && (code.length !== 6 || (mode === 'resetPassword' && newPassword.length < 8)));
  const subtitle = isCodeStep
    ? `Nhập mã 6 số vừa gửi tới ${email}.`
    : mode === 'forgot'
      ? 'Nhập email đã đăng ký, Torii sẽ gửi mã để đặt lại mật khẩu.'
      : isLogin
        ? 'Đăng nhập để tiếp tục lộ trình của bạn.'
        : 'Miễn phí. Tiến độ được đồng bộ giữa web và điện thoại.';

  return (
    <WebAuthLayout>
      <form onSubmit={submit} style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <h2 style={{ fontSize: 25, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>{HEADINGS[mode]}</h2>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '8px 0 0', lineHeight: 1.5 }}>{subtitle}</p>
        </div>

        {mode === 'register' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="displayName" style={label}>
              Tên hiển thị <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>(tuỳ chọn)</span>
            </label>
            <input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              maxLength={80}
              style={input(false)}
            />
          </div>
        )}

        {showEmail && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="email" style={label}>
              Email
            </label>
            <input
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
              autoComplete="email"
              placeholder="toriinihongo@gmail.com"
              style={input(!!errorText)}
            />
          </div>
        )}

        {showPassword && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <label htmlFor="password" style={label}>
                Mật khẩu
              </label>
              {isLogin && (
                <button type="button" onClick={() => goTo('forgot')} style={{ ...linkButton, fontSize: 12.5, color: 'var(--cta-default)' }}>
                  Quên mật khẩu?
                </button>
              )}
            </div>
            <input
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              type="password"
              required
              minLength={isLogin ? undefined : 8}
              autoComplete={isLogin ? 'current-password' : 'new-password'}
              placeholder="Ít nhất 8 ký tự"
              style={input(!!errorText)}
            />
          </div>
        )}

        {isCodeStep && timers && (
          <CodeField code={code} onChange={setCode} timers={timers} resending={resending} onResend={resend} invalid={!!errorText} />
        )}

        {mode === 'resetPassword' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label htmlFor="newPassword" style={label}>
              Mật khẩu mới
            </label>
            <input
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              placeholder="Ít nhất 8 ký tự"
              style={input(false)}
            />
          </div>
        )}

        {errorText && (
          <span role="alert" style={{ fontSize: 13, color: 'var(--error)', lineHeight: 1.45, marginTop: -8 }}>
            {errorText}
          </span>
        )}

        <button
          type="submit"
          disabled={submitDisabled}
          style={{
            height: 52,
            borderRadius: 'var(--radius-control)',
            background: 'var(--cta-default)',
            color: '#fff',
            border: 'none',
            cursor: loading ? 'progress' : submitDisabled ? 'default' : 'pointer',
            opacity: submitDisabled ? 0.6 : 1,
            fontFamily: 'var(--font-vi)',
            fontWeight: 700,
            fontSize: 16,
          }}
        >
          {loading ? 'Đang xử lý…' : SUBMIT_LABEL[mode]}
        </button>

        {(isLogin || mode === 'register') && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ flex: 1, height: 1, background: 'var(--border-default)' }} />
              <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', fontWeight: 600 }}>hoặc</span>
              <span style={{ flex: 1, height: 1, background: 'var(--border-default)' }} />
            </div>

            {GOOGLE_CLIENT_ID ? (
              // Nút Google do GIS vẽ cần cả hàng (rộng tối thiểu 200px) nên Apple xuống hàng dưới.
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <GoogleSignInButton clientId={GOOGLE_CLIENT_ID} onCredential={signInWithGoogle} onError={setErrorText} />
                <DisabledOAuthButton icon={<AppleIcon />} dark>
                  Apple
                </DisabledOAuthButton>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <DisabledOAuthButton icon={<GoogleIcon />}>Google</DisabledOAuthButton>
                <DisabledOAuthButton icon={<AppleIcon />} dark>
                  Apple
                </DisabledOAuthButton>
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 4 }}>
          <button
            type="button"
            onClick={() => goTo(isLogin || mode === 'verifyRegister' ? 'register' : 'login')}
            style={{ ...linkButton, fontSize: 14, color: 'var(--navy-500)' }}
          >
            {isLogin
              ? 'Chưa có tài khoản? Đăng ký'
              : mode === 'register'
                ? 'Đã có tài khoản? Đăng nhập'
                : mode === 'verifyRegister'
                  ? 'Đổi email đăng ký'
                  : 'Quay lại đăng nhập'}
          </button>
        </div>

        {(isLogin || mode === 'register') && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            Bằng việc tiếp tục, bạn đồng ý với{' '}
            <a href="/terms" target="_blank" rel="noreferrer" style={{ color: 'var(--cta-default)' }}>
              Điều khoản
            </a>{' '}
            &amp;{' '}
            <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--cta-default)' }}>
              Chính sách bảo mật
            </a>{' '}
            của Torii.
          </div>
        )}
      </form>
    </WebAuthLayout>
  );
}
